import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { useI18n } from 'next-localization';
import { useMemo, useState } from 'react';
import styles from './RiskWarning.module.scss';

import AnimateHeight, { Height } from 'react-animate-height';
import { RiskWarningFields } from './RiskWarning.types';
import ScLink from 'components/Generic/SitecoreWrappers/ScLink';

type RiskWarningContentProps = Partial<ComponentProps> & {
  fields: RiskWarningFields;
};

const RiskWarningContent = ({ fields, rendering }: RiskWarningContentProps): JSX.Element => {
  const { t } = useI18n();
  const [height, setHeight] = useState<Height>(0);

  const hasCta = useMemo(() => {
    return fields?.link.value;
  }, [fields?.link.value]);

  const hasDescriptionExtra = useMemo(() => {
    return fields?.descriptionExtra.value;
  }, [fields?.descriptionExtra.value]);

  const container = useMemo(() => {
    return rendering?.params ? 'container' : '';
  }, [rendering?.params]);

  const backgroundClass = useMemo(() => {
    return rendering?.params?.background || 'vectorBasedBackground';
  }, [rendering?.params?.background]);

  return (
    <section
      className={`${container} ${!container ? backgroundClass : ''} ${
        !container ? 'py-4' : ''
      } risk-warning`}
    >
      <div
        className={`${
          !container ? 'container' : `${styles['risk-warning-wrapper']} py-4 px-md-3 px-xl-5`
        } ${backgroundClass}`}
      >
        <div className="row">
          <div className="col">
            <Text className="background-to-change" tag="h5" field={fields?.title} />
            <Text className="background-to-change mt-3" tag="p" field={fields?.description} />
            {hasCta && (
              <div className="risk-warning__cta mt-2">
                <ScLink
                  className="inline-link inline-link--small background-to-change revert-underline"
                  field={fields.link}
                />
              </div>
            )}
            {hasDescriptionExtra && (
              <>
                <AnimateHeight id="description-extra" height={height} duration={500}>
                  <div className="risk-warning__additional">
                    <Text
                      className="background-to-change mt-3"
                      tag="p"
                      field={fields?.descriptionExtra}
                    />
                  </div>
                </AnimateHeight>
                <button
                  aria-expanded={height !== 0}
                  aria-controls="copyright-content"
                  className={`btn border-0 text-link p-0 mt-3 background-to-change ${
                    styles['risk-warning__additional-toggler']
                  } ${height === 0 ? 'collapsed' : 'expanded'}`}
                  type="button"
                  onClick={() => setHeight(height === 0 ? 'auto' : 0)}
                >
                  <span>{height === 0 ? t('showMore') : t('showLess')}</span>
                  <span className="chevron">
                    <i className="bi bi-chevron-down" />
                  </span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RiskWarningContent;
